import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext.hook';
import { sanitizeFileName } from '@/lib/upload';
import { validateDiligenciaFile } from './diligenciaDocumentsConstants';

export type DiligenciaDocumentReviewStatus = 'pendente' | 'aprovado' | 'reprovado';

/**
 * Resultado de um upload em lote.
 *
 * Constantes públicas relacionadas (MAX_FILES_PER_CHECKLIST, MAX_FILE_SIZE_MB,
 * etc.) ficam em `./diligenciaDocumentsConstants` para permitir HMR
 * adequado neste arquivo de hook (react-refresh/only-export-components).
 */
export interface MultiUploadResult {
  uploaded: DiligenciaDocument[];
  failed: Array<{ file: File; error: string }>;
}

export interface DiligenciaDocument {
  id: string;
  diligencia_id: string;
  etapa: string;
  checklist_key: string;
  file_name: string;
  file_path: string;
  file_size: number | null;
  mime_type: string | null;
  uploaded_by: string;
  created_at: string;
  sign_status: 'pendente' | 'enviado' | 'assinado' | 'recusado' | null;
  clicksign_envelope_id?: string | null;
  clicksign_document_id?: string | null;
  clicksign_request_url?: string | null;
  review_status: DiligenciaDocumentReviewStatus;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  review_note?: string | null;
}

interface DeleteDocumentOptions {
  silent?: boolean;
}

export const useDiligenciaDocuments = (diligenciaId: string | undefined) => {
  const [documents, setDocuments] = useState<DiligenciaDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const fetchDocuments = useCallback(async () => {
    if (!diligenciaId) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('diligencia_documents')
        .select('*')
        .eq('diligencia_id', diligenciaId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (err) {
      console.error('Erro ao buscar documentos:', err);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os documentos.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [diligenciaId, toast]);

  const getDocumentsByEtapa = useCallback(
    (etapa: string) => documents.filter((d) => d.etapa === etapa),
    [documents]
  );

  const getDocumentByChecklistKey = useCallback(
    (etapa: string, checklistKey: string) =>
      documents.find((d) => d.etapa === etapa && d.checklist_key === checklistKey),
    [documents]
  );

  const getSignatureDocuments = useCallback(
    () => documents.filter((d) => d.sign_status !== null),
    [documents]
  );

  /**
   * Persiste um único arquivo (Storage + linha em diligencia_documents).
   *
   * Função interna usada por `uploadDocument` (single) e `uploadDocuments`
   * (batch). Não emite toast nem mexe em `setIsUploading` — quem chama é
   * responsável por isso, permitindo agregação de mensagens em multi-upload.
   */
  const persistOne = useCallback(
    async (
      etapa: string,
      checklistKey: string,
      file: File,
      signStatus?: 'pendente' | 'assinado'
    ): Promise<DiligenciaDocument> => {
      if (!diligenciaId || !user) {
        throw new Error('Sessão expirada. Faça login novamente.');
      }

      const timestamp = Date.now();
      const sanitized = sanitizeFileName(file.name);
      const filePath = `${diligenciaId}/${etapa}/${checklistKey}/${timestamp}_${sanitized}`;

      const { error: uploadError } = await supabase.storage
        .from('diligencia-documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data, error: insertError } = await supabase
        .from('diligencia_documents')
        .insert({
          diligencia_id: diligenciaId,
          etapa,
          checklist_key: checklistKey,
          file_name: file.name,
          file_path: filePath,
          file_size: file.size,
          mime_type: file.type,
          uploaded_by: user.id,
          review_status: 'pendente',
          reviewed_by: null,
          reviewed_at: null,
          review_note: null,
          ...(signStatus ? { sign_status: signStatus } : {}),
        })
        .select()
        .single();

      if (insertError) {
        // Limpa o blob órfão do storage se o INSERT falhar
        await supabase.storage
          .from('diligencia-documents')
          .remove([filePath])
          .catch(() => undefined);
        throw insertError;
      }

      return data as DiligenciaDocument;
    },
    [diligenciaId, user]
  );

  /**
   * Upload de um único arquivo (mantido por retrocompatibilidade).
   */
  const uploadDocument = async (
    etapa: string,
    checklistKey: string,
    file: File,
    signStatus?: 'pendente' | 'assinado'
  ): Promise<DiligenciaDocument | null> => {
    if (!diligenciaId || !user) return null;

    setIsUploading(true);
    try {
      const data = await persistOne(etapa, checklistKey, file, signStatus);

      setDocuments((prev) => [data, ...prev]);

      toast({
        title: 'Documento enviado',
        description: `${file.name} foi enviado com sucesso.`,
      });

      return data;
    } catch (err) {
      console.error('Erro ao enviar documento:', err);
      toast({
        title: 'Erro no upload',
        description:
          err instanceof Error
            ? err.message
            : 'Não foi possível enviar o documento.',
        variant: 'destructive',
      });
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * Upload de múltiplos arquivos para o mesmo checklist key (S4.2).
   *
   * - Valida client-side (tamanho/MIME) ANTES do upload — arquivos
   *   inválidos vão direto para `failed` sem tocar a rede.
   * - Usa Promise.allSettled para tolerar falha parcial (se 1 falha,
   *   os outros continuam).
   * - Emite UM toast agregado ao final.
   * - Caller deve checar `result.failed` para retry seletivo.
   */
  const uploadDocuments = async (
    etapa: string,
    checklistKey: string,
    files: File[]
  ): Promise<MultiUploadResult> => {
    const result: MultiUploadResult = { uploaded: [], failed: [] };

    if (files.length === 0) return result;
    if (!diligenciaId || !user) {
      files.forEach((file) =>
        result.failed.push({
          file,
          error: 'Sessão expirada. Faça login novamente.',
        })
      );
      return result;
    }

    // Validação client-side
    const toUpload: File[] = [];
    for (const file of files) {
      const error = validateDiligenciaFile(file);
      if (error) {
        result.failed.push({ file, error });
      } else {
        toUpload.push(file);
      }
    }

    if (toUpload.length === 0) {
      // Apenas mostra erros de validação
      toast({
        title: 'Nenhum arquivo válido',
        description: result.failed
          .map((f) => f.error)
          .slice(0, 3)
          .join(' '),
        variant: 'destructive',
      });
      return result;
    }

    setIsUploading(true);
    try {
      const settled = await Promise.allSettled(
        toUpload.map((file) => persistOne(etapa, checklistKey, file))
      );

      settled.forEach((outcome, idx) => {
        const file = toUpload[idx];
        if (outcome.status === 'fulfilled') {
          result.uploaded.push(outcome.value);
        } else {
          const reason = outcome.reason;
          const message =
            reason instanceof Error
              ? reason.message
              : 'Falha ao enviar.';
          result.failed.push({ file, error: message });
        }
      });

      if (result.uploaded.length > 0) {
        setDocuments((prev) => [...result.uploaded, ...prev]);
      }

      // Toast agregado
      if (result.uploaded.length > 0 && result.failed.length === 0) {
        toast({
          title: 'Arquivos enviados',
          description: `${result.uploaded.length} arquivo(s) enviado(s) com sucesso.`,
        });
      } else if (result.uploaded.length > 0 && result.failed.length > 0) {
        toast({
          title: 'Envio parcial',
          description: `${result.uploaded.length} enviado(s), ${result.failed.length} falharam. Tente novamente os com erro.`,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Falha no envio',
          description:
            result.failed[0]?.error || 'Não foi possível enviar os arquivos.',
          variant: 'destructive',
        });
      }

      return result;
    } finally {
      setIsUploading(false);
    }
  };

  const deleteDocument = async (
    docId: string,
    options?: DeleteDocumentOptions
  ): Promise<boolean> => {
    try {
      const doc = documents.find((d) => d.id === docId);
      if (!doc) return false;

      // Remover do Storage
      const { error: storageError } = await supabase.storage
        .from('diligencia-documents')
        .remove([doc.file_path]);

      if (storageError) console.warn('Erro ao remover do storage:', storageError);

      // Remover do banco
      const { error: deleteError } = await supabase
        .from('diligencia_documents')
        .delete()
        .eq('id', docId);

      if (deleteError) throw deleteError;

      setDocuments((prev) => prev.filter((d) => d.id !== docId));

      if (!options?.silent) {
        toast({
          title: 'Documento removido',
          description: 'O documento foi removido com sucesso.',
        });
      }

      return true;
    } catch (err) {
      console.error('Erro ao remover documento:', err);
      toast({
        title: 'Erro',
        description: 'Não foi possível remover o documento.',
        variant: 'destructive',
      });
      return false;
    }
  };

  const getSignedUrl = async (filePath: string): Promise<string | null> => {
    try {
      const { data, error } = await supabase.storage
        .from('diligencia-documents')
        .createSignedUrl(filePath, 3600);

      if (error) throw error;
      return data.signedUrl;
    } catch (err) {
      console.error('Erro ao gerar URL:', err);
      toast({
        title: 'Erro ao abrir documento',
        description: 'Não foi possível gerar o link seguro para visualizar o arquivo.',
        variant: 'destructive',
      });
      return null;
    }
  };

  const updateDocumentReview = async (
    docId: string,
    status: DiligenciaDocumentReviewStatus,
    note?: string | null
  ): Promise<boolean> => {
    if (!user) return false;

    try {
      const payload = {
        review_status: status,
        reviewed_by: user.id,
        reviewed_at: new Date().toISOString(),
        review_note: note?.trim() ? note.trim() : null,
      };

      const { data, error } = await supabase
        .from('diligencia_documents')
        .update(payload)
        .eq('id', docId)
        .select()
        .single();

      if (error) throw error;

      setDocuments((prev) =>
        prev.map((doc) => (doc.id === docId ? { ...doc, ...data } : doc))
      );

      toast({
        title: status === 'aprovado' ? 'Documento aprovado' : 'Documento reprovado',
        description:
          status === 'aprovado'
            ? 'A revisão do documento foi concluída com sucesso.'
            : 'O documento foi marcado para reenvio.',
      });

      return true;
    } catch (err) {
      console.error('Erro ao revisar documento:', err);
      toast({
        title: 'Erro na revisão',
        description: 'Não foi possível atualizar o status do documento.',
        variant: 'destructive',
      });
      return false;
    }
  };

  const getCompletionByEtapa = useCallback(
    (etapa: string, totalItems: number) => {
      if (totalItems === 0) return { completed: 0, total: 0, percentage: 100 };
      const etapaDocs = documents.filter((d) => d.etapa === etapa);
      const uniqueKeys = new Set(etapaDocs.map((d) => d.checklist_key));
      const completed = uniqueKeys.size;
      return {
        completed,
        total: totalItems,
        percentage: Math.round((completed / totalItems) * 100),
      };
    },
    [documents]
  );

  return {
    documents,
    isLoading,
    isUploading,
    fetchDocuments,
    getDocumentsByEtapa,
    getDocumentByChecklistKey,
    getSignatureDocuments,
    uploadDocument,
    uploadDocuments,
    deleteDocument,
    getSignedUrl,
    updateDocumentReview,
    getCompletionByEtapa,
  };
};
